import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createBusinessPersonalitySettings, updateBusinessPersonalitySettings, getBusinessPersonalitySettings, getEmbeddedSignupByPhone, saveKnowledgeVectors, listDocuments, deleteDocument } from '../../../api/login.service';
import { useAuth } from '../../../authContext';
import BotTrainerPhone from './BotTrainerPhone';
import LoadingComponent from '../../../components/LoadingComponent';
import { 
  IoMdCloudUpload, 
  IoMdDocument, 
  IoMdTrash, 
  IoMdClose,
  IoMdCheckmarkCircle,
  IoMdAlert,
  IoMdTime
} from 'react-icons/io';

const DEFAULT_ANSWER_STYLE =
  'Answer in 2 or 3 short lines, the way a person would text. Understand the question first. Answer only what was asked. Do not add information that is not relevant. Do not ask extra questions they did not ask. If they already gave a name or number, do not ask again.';

interface BusinessPersonalitySettings {
  businessName: string;
  mainSystemPrompt: string;
  defaultTone: 'professional' | 'friendly' | 'casual' | 'formal' | 'conversational';
  businessContext: {
    industry: string;
    businessRules: string[];
    customerProfile: string;
    brandVoice: string;
    answerStyle: string;
    doNotAnswer: string;
    questionsToAsk: string;
    leadGoal: string;
  };
  languageSettings: {
    defaultLanguage: string;
    supportedLanguages: string[];
    autoDetectLanguage: boolean;
  };
  aiModelSettings: {
    defaultModel: string;
    defaultTemperature: number;
    defaultMaxTokens: number;
    defaultTopP: number;
  };
  businessNumber?: string;
  wabaId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export default function ModernBusinessPersonalitySettings() {
  const navigate = useNavigate();
  const { displayPhoneNumber, email, verifiedName } = useAuth();
  
  // Get wabaId from session storage
  const wabaId = sessionStorage.getItem('wabaId');
  
  // Get business phone number from multiple sources
  const businessPhoneNumber = displayPhoneNumber || email || sessionStorage.getItem('phone') || sessionStorage.getItem('display_phone_number') || '';
  
  const [settings, setSettings] = useState<BusinessPersonalitySettings>({
    businessName: verifiedName || '',
    mainSystemPrompt: '',
    defaultTone: 'professional',
    businessContext: {
      industry: '',
      businessRules: [''],
      customerProfile: '',
      brandVoice: '',
      answerStyle: DEFAULT_ANSWER_STYLE,
      doNotAnswer: '',
      questionsToAsk: '',
      leadGoal: '',
    },
    languageSettings: {
      defaultLanguage: 'en',
      supportedLanguages: ['en'],
      autoDetectLanguage: true
    },
    aiModelSettings: {
      defaultModel: 'gpt-4',
      defaultTemperature: 0.7,
      defaultMaxTokens: 2000,
      defaultTopP: 0.9
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Document upload states
  const [phoneNumberId, setPhoneNumberId] = useState<string>('');
  const [documents, setDocuments] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing settings on component mount
  useEffect(() => {
    loadBusinessSettings();
    loadPhoneNumberId();
  }, []);

  // Load documents when phoneNumberId is available
  useEffect(() => {
    if (phoneNumberId) {
      loadDocuments();
    }
  }, [phoneNumberId]);

  // Debug: Log settings changes
  useEffect(() => {
    console.log('Settings updated:', settings);
  }, [settings]);

  const loadPhoneNumberId = async () => {
    try {
      if (!businessPhoneNumber) return;
      
      const response = await getEmbeddedSignupByPhone(businessPhoneNumber);
      const records = response.success && response.records ? response.records : [];
      const record = records[0];
      if (record?.phoneNumberId) {
        setPhoneNumberId(record.phoneNumberId);
      }
    } catch (error) {
      console.error('Error loading phone number ID:', error);
    }
  };

  const loadBusinessSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getBusinessPersonalitySettings(businessPhoneNumber);
      
      if (response.success && response.botSettings) {
        console.log('Bot settings loaded:', response.botSettings);
        const loaded = response.botSettings;
        setSettings(prev => ({
          ...prev,
          ...loaded,
          businessName: loaded.businessName || verifiedName || 'My Business',
          businessContext: {
            ...prev.businessContext,
            ...(loaded.businessContext || {}),
            answerStyle: loaded.businessContext?.answerStyle || DEFAULT_ANSWER_STYLE,
            doNotAnswer: loaded.businessContext?.doNotAnswer || '',
            questionsToAsk: loaded.businessContext?.questionsToAsk || '',
            leadGoal: loaded.businessContext?.leadGoal || loaded.leadGoal || '',
          },
        }));
      } else {
        console.log('No bot settings found in response:', response);
      }
    } catch (error) {
      console.error('Error loading business personality settings:', error);
      // Don't set error state for loading failures, just use defaults
    } finally {
      setLoading(false);
    }
  };

  const knowledgeKey = (id: string) => `knowledge-file:${id}`;

  const rememberedKnowledge = (id: string) => {
    try {
      const raw = localStorage.getItem(knowledgeKey(id));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const rememberKnowledge = (id: string, doc: { fileName: string; uploadDate: string; fileSize: number; pages: number; chunksStored: number }) => {
    localStorage.setItem(knowledgeKey(id), JSON.stringify(doc));
  };

  const loadDocuments = async () => {
    if (!phoneNumberId) return;
    const remembered = rememberedKnowledge(phoneNumberId);

    try {
      const response = await listDocuments(phoneNumberId);

      if (response.success && ((response.documents && response.documents.length > 0) || response.hasDocuments || response.chunkCount > 0)) {
        const saved = response.documents && response.documents[0];
        const doc = {
          fileName: saved?.fileName || response.fileName || remembered?.fileName || 'Uploaded knowledge',
          uploadDate: saved?.uploadDate || remembered?.uploadDate || new Date().toISOString(),
          fileSize: saved?.fileSize || remembered?.fileSize || 0,
          pages: saved?.pages || remembered?.pages || 0,
          chunksStored: saved?.chunksStored || response.chunkCount || remembered?.chunksStored || 0,
        };
        setDocuments([doc]);
        rememberKnowledge(phoneNumberId, doc);
      } else if (response.success) {
        localStorage.removeItem(knowledgeKey(phoneNumberId));
        setDocuments([]);
      } else if (remembered) {
        setDocuments([remembered]);
      } else {
        setDocuments([]);
      }
    } catch (err: any) {
      console.error('Error loading documents:', err);
      setDocuments(remembered ? [remembered] : []);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const wabaIdToUse = wabaId || 'default';
      let response;
      
      // Check if settings already exist to determine POST vs PUT
      try {
        const existingSettings = await getBusinessPersonalitySettings(businessPhoneNumber);
        console.log('Existing settings response:', existingSettings);
        console.log('existingSettings.success:', existingSettings.success);
        console.log('existingSettings.data:', existingSettings.data);
        console.log('existingSettings.botSettings:', existingSettings.botSettings);
        
        // Check if settings exist
        const hasSettings = existingSettings.success && (existingSettings.data || existingSettings.botSettings);
        
        if (hasSettings) {
          // Settings EXIST - use PUT to update
          console.log('✅ Settings exist, using UPDATE (PUT)');
          const settingsData = existingSettings.data || existingSettings.botSettings;
          const wabaIdFromSettings = settingsData?.wabaId || wabaIdToUse;
          console.log('Using wabaId:', wabaIdFromSettings);
          response = await updateBusinessPersonalitySettings(businessPhoneNumber, wabaIdFromSettings, settings);
        } else {
          // Settings DON'T EXIST - use POST to create
          console.log('❌ Settings don\'t exist, using CREATE (POST)');
          console.log('Using wabaId:', wabaIdToUse);
          response = await createBusinessPersonalitySettings(businessPhoneNumber, wabaIdToUse, settings);
        }
      } catch (getError: any) {
        // If GET fails (e.g., 404), assume settings don't exist and create new ones
        console.log('⚠️ Could not check existing settings (likely 404), using CREATE (POST)');
        console.log('Get error:', getError);
        console.log('Using wabaId:', wabaIdToUse);
        response = await createBusinessPersonalitySettings(businessPhoneNumber, wabaIdToUse, settings);
      }
      
      if (response.success) {
        setSuccess('Business personality settings saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(response.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving business personality settings:', error);
      setError('Failed to save business personality settings');
    } finally {
      setSaving(false);
    }
  };

  const addBusinessRule = () => {
    setSettings(prev => ({
      ...prev,
      businessContext: {
        ...prev.businessContext,
        businessRules: [...prev.businessContext.businessRules, '']
      }
    }));
  };

  const updateBusinessRule = (index: number, value: string) => {
    setSettings(prev => ({
      ...prev,
      businessContext: {
        ...prev.businessContext,
        businessRules: prev.businessContext.businessRules.map((rule, i) => 
          i === index ? value : rule
        )
      }
    }));
  };

  const removeBusinessRule = (index: number) => {
    setSettings(prev => ({
      ...prev,
      businessContext: {
        ...prev.businessContext,
        businessRules: prev.businessContext.businessRules.filter((_, i) => i !== index)
      }
    }));
  };

  // Document upload handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError(`File size exceeds 10MB limit. Selected file: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    setSelectedFile(file);
    setError(null);
    setSuccess(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !phoneNumberId) {
      setError('Please select a file and ensure phone number ID is available');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const vectors = await saveKnowledgeVectors(selectedFile, phoneNumberId);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (vectors.success) {
        const doc = {
          fileName: selectedFile.name,
          uploadDate: new Date().toISOString(),
          fileSize: selectedFile.size,
          pages: vectors.pages || 0,
          chunksStored: vectors.chunksStored || 0,
        };
        setDocuments([doc]);
        rememberKnowledge(phoneNumberId, doc);
        setSuccess(`File uploaded. ${doc.chunksStored} knowledge chunks saved.`);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error('Upload failed');
      }
    } catch (err: any) {
      console.error('Error uploading document:', err);
      setError(err?.message || 'Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteDocument = async () => {
    if (!phoneNumberId) {
      setError('Phone number ID not found');
      return;
    }

    if (!window.confirm('Are you sure you want to delete the uploaded document? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDocument(phoneNumberId);
      localStorage.removeItem(knowledgeKey(phoneNumberId));
      setDocuments([]);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setSuccess('Knowledge removed. You can upload another PDF.');
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting document:', err);
      setError(err?.message || 'Failed to delete document');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return <LoadingComponent loading={true} message="Loading business personality settings..." />;
  }

  return (
    <div className="flex flex-col gap-8 xl:flex-row xl:items-start">
    <div className="min-w-0 flex-1 space-y-8">
      {/* Page Description */}
      <div className="mb-8">
        <p className="text-lg text-gray-600">Configure your AI assistant's personality and behavior for customer interactions</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
            </svg>
            <p className="text-green-800 font-medium">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
            </svg>
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Basic Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
            <input
              type="text"
              value={settings.businessName}
              onChange={(e) => setSettings(prev => ({ ...prev, businessName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your business name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Tone</label>
            <select
              value={settings.defaultTone}
              onChange={(e) => setSettings(prev => ({ ...prev, defaultTone: e.target.value as any }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="casual">Casual</option>
              <option value="formal">Formal</option>
              <option value="conversational">Conversational</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Lead goal</h3>
        <p className="text-sm text-gray-500 mb-4">
          What should a lead do for this business? The score uses this text. Another business can write a different goal.
        </p>
        <textarea
          value={settings.businessContext.leadGoal}
          onChange={(e) => setSettings(prev => ({
            ...prev,
            businessContext: { ...prev.businessContext, leadGoal: e.target.value },
          }))}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Example: The person joins a dance class at this studio."
        />
      </div>

      {/* System Prompt */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">System Prompt</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Main System Prompt</label>
          <textarea
            value={settings.mainSystemPrompt}
            onChange={(e) => setSettings(prev => ({ ...prev, mainSystemPrompt: e.target.value }))}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Define how your AI assistant should behave and respond to customers..."
          />
          <p className="text-sm text-gray-500 mt-2">
            The WhatsApp bot and the Instagram bot use this prompt, how to answer, what to ask, what not to answer, the brand voice, and the business rules saved here.
          </p>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">How to answer</label>
          <textarea
            value={settings.businessContext.answerStyle}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              businessContext: { ...prev.businessContext, answerStyle: e.target.value }
            }))}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="How long a reply should be, and what to leave out."
          />
          <p className="text-sm text-gray-500 mt-2">
            Saved for this business. WhatsApp and Instagram both use this text. Change it here for this business only.
          </p>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">What not to answer</label>
          <textarea
            value={settings.businessContext.doNotAnswer}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              businessContext: { ...prev.businessContext, doNotAnswer: e.target.value }
            }))}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Questions or topics this business does not want the bot to ask or answer."
          />
          <p className="text-sm text-gray-500 mt-2">
            Saved for this business only. Another business can write a different note. Leave it empty if there is nothing to block.
          </p>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">What to ask</label>
          <textarea
            value={settings.businessContext.questionsToAsk}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              businessContext: { ...prev.businessContext, questionsToAsk: e.target.value }
            }))}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Questions this business wants asked, such as a name or a WhatsApp number."
          />
          <p className="text-sm text-gray-500 mt-2">
            Saved for this business only. Another business can ask different questions. Leave it empty to ask nothing extra.
          </p>
        </div>
      </div>

      {/* Business Context */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Business Context</h3>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
            <input
              type="text"
              value={settings.businessContext.industry}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                businessContext: { ...prev.businessContext, industry: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., E-commerce, Healthcare, Education"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Customer Profile</label>
            <textarea
              value={settings.businessContext.customerProfile}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                businessContext: { ...prev.businessContext, customerProfile: e.target.value }
              }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Describe your typical customers..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Brand Voice</label>
            <textarea
              value={settings.businessContext.brandVoice}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                businessContext: { ...prev.businessContext, brandVoice: e.target.value }
              }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Describe your brand's personality and communication style..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business Rules</label>
            <div className="space-y-3">
              {settings.businessContext.businessRules.map((rule, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <input
                    type="text"
                    value={rule}
                    onChange={(e) => updateBusinessRule(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`Business rule ${index + 1}`}
                  />
                  {settings.businessContext.businessRules.length > 1 && (
                    <button
                      onClick={() => removeBusinessRule(index)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addBusinessRule}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                + Add Rule
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Language Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Language Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Language</label>
            <select
              value={settings.languageSettings.defaultLanguage}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                languageSettings: { ...prev.languageSettings, defaultLanguage: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="it">Italian</option>
              <option value="pt">Portuguese</option>
              <option value="zh">Chinese</option>
              <option value="ja">Japanese</option>
              <option value="ko">Korean</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoDetectLanguage"
              checked={settings.languageSettings.autoDetectLanguage}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                languageSettings: { ...prev.languageSettings, autoDetectLanguage: e.target.checked }
              }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="autoDetectLanguage" className="ml-2 text-sm font-medium text-gray-700">
              Auto-detect customer language
            </label>
          </div>
        </div>
      </div>

      {/* Save Button for Settings */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Knowledge Base Upload Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center space-x-2">
          <IoMdDocument className="w-5 h-5 text-blue-600" />
          <span>Knowledge Base Upload</span>
        </h3>
        
        <div className="space-y-4">
          {/* Documents List - Show first if documents exist */}
          {documents.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-900">Uploaded Documents</h4>
                <button
                  onClick={loadDocuments}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Refresh
                </button>
              </div>
              {documents.map((doc, index) => (
                <div
                  key={doc.id || index}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <IoMdDocument className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {doc.fileName}
                      </p>
                      <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                        {doc.fileSize > 0 && <span>{formatFileSize(doc.fileSize)}</span>}
                        {doc.pages > 0 && <span>{doc.pages} {doc.pages === 1 ? 'page' : 'pages'}</span>}
                        {doc.chunksStored > 0 && <span>{doc.chunksStored} {doc.chunksStored === 1 ? 'chunk' : 'chunks'}</span>}
                        <span className="flex items-center space-x-1">
                          <IoMdTime className="w-3 h-3" />
                          <span>{formatDate(doc.uploadDate)}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleDeleteDocument}
                    className="ml-3 inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                    title="Delete knowledge"
                  >
                    <IoMdTrash className="w-5 h-5" />
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400 bg-gray-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-3">
                  <IoMdDocument className="w-8 h-8 text-blue-600" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <IoMdClose className="w-5 h-5" />
                  </button>
                </div>
                
                {isUploading && (
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600">Uploading... {uploadProgress}%</p>
                  </div>
                )}
                
                {!isUploading && selectedFile && (
                  <p className="text-xs text-gray-500">File selected. Click "Save Document" button below to upload.</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <IoMdCloudUpload className="w-12 h-12 text-gray-400 mx-auto" />
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    Drag and drop a file here, or click to browse
                  </p>
                  <p className="text-xs text-gray-500">PDF, DOC, DOCX, TXT (Max 10MB)</p>
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Choose File
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleFileInput}
                  className="hidden"
                />
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <IoMdAlert className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800">
                <p className="font-medium mb-1">Knowledge Base Document</p>
                <p>The system prompt, brand voice, and business rules stay in the business settings. This uploaded file is stored as S3 vectors. A WhatsApp or Instagram message searches those vectors and answers with the matching passages.</p>
                <p className="mt-2 text-blue-700">Note: You can upload one document per phone number. Uploading a new document will replace the existing one.</p>
              </div>
            </div>
          </div>

          {/* Save Button for Document Upload */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={handleUpload}
              disabled={!selectedFile || !phoneNumberId || isUploading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isUploading ? 'Uploading...' : 'Save Document'}
            </button>
          </div>
        </div>
      </div>
    </div>
    <BotTrainerPhone
      businessName={settings.businessName}
      phoneNumberId={phoneNumberId}
      businessNumber={businessPhoneNumber}
      wabaId={wabaId || ''}
    />
    </div>
  );
}
